import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { useLanguage } from '../contexts/LanguageContext';

interface Option {
  value: string;
  label: string;
  description?: string;
}

interface QuestionCardProps {
  question: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  onNext?: () => void;
  onPrev?: () => void;
}

export function QuestionCard({ question, options, value, onChange, onNext, onPrev }: QuestionCardProps) {
  const { translations: t } = useLanguage();
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const focusedIndexRef = useRef<number>(-1);
  
  // Keep ref in sync with state
  useEffect(() => {
    focusedIndexRef.current = focusedIndex;
  }, [focusedIndex]);
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // IGNORE keyboard shortcuts if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable
      ) {
        return; // Don't handle keyboard shortcuts when typing
      }

      // Arrow navigation
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(prev => {
          const newIndex = prev === -1 ? 0 : Math.min(prev + 1, options.length - 1);
          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex(prev => {
          const newIndex = prev === -1 ? options.length - 1 : Math.max(prev - 1, 0);
          return newIndex;
        });
      } 
      // Enter key - if option is selected, advance; if option is focused, select it
      else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const currentFocusedIndex = focusedIndexRef.current;
        
        // If something is already selected, advance
        if (value) {
          if (onNext) {
            onNext();
          }
        }
        // If something is focused but not selected, select it
        else if (currentFocusedIndex >= 0 && currentFocusedIndex < options.length) {
          onChange(options[currentFocusedIndex].value);
        }
      }
      // Number keys (1-5) - only select, don't advance
      else if (e.key >= '1' && e.key <= '5') {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (index < options.length) {
          onChange(options[index].value);
        }
      }
      // Backspace or Delete - go back to previous step
      else if ((e.key === 'Backspace' || e.key === 'Delete') && onPrev) {
        e.preventDefault();
        onPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [options, onChange, onNext, onPrev, value]);

  // Reset focused index when question changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [question]);
  
  return (
    <div className="space-y-6">
      <motion.h2 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-2xl md:text-3xl font-medium text-white text-center mb-8"
      >
        {question}
      </motion.h2>
      
      <RadioGroup value={value} onValueChange={onChange} className="space-y-3 md:space-y-[5px]">
        {options.map((option, index) => {
          const isSelected = value === option.value;
          const isFocused = focusedIndex === index;
          const keyNumber = index + 1;
          return (
            <div
              key={option.value}
              className={`group relative flex items-center space-x-1.5 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                isSelected
                  ? 'border-[#90d977] bg-[#90d977]/10'
                  : isFocused
                  ? 'border-[#90d977]/50 bg-[#90d977]/5'
                  : 'border-[#27272a] bg-[#0d0d12] hover:border-[#90d977] hover:bg-[#90d977]'
              }`}
              onClick={() => onChange(option.value)}
              onMouseEnter={() => {
                if (window.innerWidth >= 768) {
                  setFocusedIndex(index);
                }
              }}
              onMouseLeave={() => {
                if (window.innerWidth >= 768) {
                  setFocusedIndex(-1);
                }
              }}
            >
              {/* Keyboard hint badge - Hidden on mobile */}
              <div className={`hidden md:flex flex-shrink-0 items-center justify-center w-6 h-6 rounded border text-xs transition-all ${
                isSelected 
                  ? 'border-[#90d977] bg-[#90d977] text-black' 
                  : isFocused
                  ? 'border-[#90d977] bg-black text-[#90d977]'
                  : 'border-[#3f3f46] bg-[#18181b] text-[#a1a1aa] group-hover:border-[#90d977] group-hover:bg-black group-hover:text-white'
              }`}>
                {keyNumber}
              </div>
              <RadioGroupItem value={option.value} id={option.value} className={`flex-shrink-0 ${isSelected ? 'opacity-0' : ''}`} />
              <Label
                htmlFor={option.value}
                className="flex-1 cursor-pointer"
              >
                <div>
                  <span className={`${isSelected ? 'text-white' : isFocused ? 'text-white' : 'text-white group-hover:text-black'}`}>
                    {option.label}
                  </span>
                  {option.description && (
                    <span className={`text-sm font-normal ml-2 ${isSelected ? 'text-[#d1d5db]' : isFocused ? 'text-[#d1d5db]' : 'text-[#a1a1aa] group-hover:text-black/80'}`}>
                      ({option.description})
                    </span>
                  )}
                </div>
              </Label>
            </div>
          );
        })}
      </RadioGroup>
      
      {/* Keyboard hint - Hidden on mobile */}
      <div className="hidden md:block text-center text-sm text-[#a1a1aa] mt-6">
        {t.questionCard.keyboardHint.split('{numbers}')[0]}
        <kbd className="px-2 py-1 bg-[#18181b] border border-[#3f3f46] rounded text-[#90d977] mx-1">1-{options.length}</kbd>
        {t.questionCard.keyboardHint.split('{numbers}')[1].split('{enter}')[0]}
        <kbd className="px-2 py-1 bg-[#18181b] border border-[#3f3f46] rounded text-[#90d977] mx-1">↑↓</kbd>
        <span className="mx-1">+</span>
        <kbd className="px-2 py-1 bg-[#18181b] border border-[#3f3f46] rounded text-[#90d977] mx-1">Enter</kbd>
        {t.questionCard.keyboardHint.split('{enter}')[1]}
      </div>
    </div>
  );
}